/* ---------------------------------------------------------------------------
   Compare bikes.

   Three parts: the buttons (buy grid and product tiles), the tray that holds
   the picks, and the panel that lays them out side by side. Markup and styling
   for the tray and panel live in sections/lux-compare.liquid, which renders in
   the overlay group so this runs on every page.

   Picks are variant level, not product level. "Nova 20.5" and "Nova 20" are
   different bikes to a shopper choosing a size, and the price, stock and top
   tube all move with the variant.

   Values come from /products/<handle>?view=compare, because metafields can only
   be read in Liquid. Nothing is cached across page loads on purpose: the table
   quotes stock and price, and a cached column is a column that lies.

   The panel has a second tab, Find bikes, which fills the same table from a
   whole category: Freestyle or Race, a size, a price range, a top tube range
   on the sizes with enough bikes to need one, and an in-stock switch (off by
   default: everything the store can sell shows, and the switch narrows it to
   the shelf). It reads /collections/<handle>?view=compare, one fetch per
   category per page load, and filters in the browser - every colour and size
   that fits becomes a column, most popular first (or by price or age, the
   shopper's call), so the shopper compares the actual bikes they could buy
   rather than a "from" price on a parent. Columns
   carry the same tick as a product tile, so a bike found here can be dropped
   into the picks and kept while the filters move on. The sliders sit outside
   the part of the filter bar that folds, so the budget can be nudged while
   the columns are being read.

   Spec values that match a glossary term (compare_glossary metaobjects, passed
   in through the config) get a dotted underline, and tapping one opens a
   small popup with a sentence or two on what it means. A shopper who does not
   know what chromoly or a freecoaster is finds out without leaving the table,
   and nothing in the table links away from it. The availability line gets
   the same treatment: its popup says when the order would leave, quoting
   the dispatch estimate the product page shows (custom.js shares its engine
   as window.luxDispatch).
   --------------------------------------------------------------------------- */
(function () {
    'use strict';

    var KEY = 'lux-compare';
    var DIFF_KEY = 'lux-compare-diff';
    var COLLAPSE_KEY = 'lux-compare-collapsed';
    var PILL_KEY = 'lux-compare-pill';

    var root = document.querySelector('[data-lux-compare]');
    if (!root) return;

    var config = { max: 3, rows: [], labels: {}, differencesDefault: false, find: {} };
    try {
        var configEl = root.querySelector('[data-cmp-config]');
        if (configEl) config = JSON.parse(configEl.textContent);
    } catch (e) {
        return;
    }
    var labels = config.labels || {};
    var findCfg = config.find || {};
    var availCfg = config.avail || {};
    var glossary = buildGlossary(config.glossary);

    var tray = root.querySelector('[data-cmp-tray]');
    var collapseBtn = root.querySelector('[data-cmp-collapse]');
    var listEl = root.querySelector('[data-cmp-list]');
    var countEl = root.querySelector('[data-cmp-count]');
    var hintEl = root.querySelector('[data-cmp-hint]');
    var openBtn = root.querySelector('[data-cmp-open]');
    var openLabel = root.querySelector('[data-cmp-open-label]');
    var panel = root.querySelector('[data-cmp-panel]');
    var scrim = root.querySelector('[data-cmp-scrim]');
    var tableEl = root.querySelector('[data-cmp-table]');
    var diffInput = root.querySelector('[data-cmp-diff]');
    var toolsEl = root.querySelector('.lux-cmp__panel-tools');
    var reorderHint = root.querySelector('[data-cmp-reorder-hint]');
    var scrollNav = root.querySelector('[data-cmp-scroll-nav]');
    var scrollPrev = scrollNav ? scrollNav.querySelector('[data-cmp-scroll="-1"]') : null;
    var scrollNext = scrollNav ? scrollNav.querySelector('[data-cmp-scroll="1"]') : null;

    /* Find bikes. Every element is optional: the tab can be switched off in
       the theme editor, and the rest of this file must not care. */
    var findBar = root.querySelector('[data-cmp-find-bar]');
    var findOn = !!(findCfg.enabled && findBar && tableEl);
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-cmp-tab]'));
    var tabCount = root.querySelector('[data-cmp-tab-count]');
    var pill = root.querySelector('[data-cmp-pill]');
    var findFilters = root.querySelector('[data-find-filters]');
    var findToggle = root.querySelector('[data-find-toggle]');
    var findSummary = root.querySelector('[data-find-summary]');
    var findToggleLabel = root.querySelector('[data-find-toggle-label]');
    var findStatus = root.querySelector('[data-find-status]');
    var sortSelect = root.querySelector('[data-find-sort]');
    var sortWrap = root.querySelector('[data-find-sort-wrap]');
    var typeInputs = Array.prototype.slice.call(root.querySelectorAll('[data-find-type]'));
    var stockInput = root.querySelector('[data-find-stock]');
    var sizeGroups = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-find-sizes]'), function (el) {
        sizeGroups[el.dataset.findSizes] = el;
    });
    var presetBtns = Array.prototype.slice.call(root.querySelectorAll('[data-find-preset]'));
    var ttGroup = root.querySelector('[data-find-tt-group]');
    var guidePop = root.querySelector('[data-find-guide-pop]');
    var guideBody = root.querySelector('[data-find-guide-body]');
    var guideCard = root.querySelector('[data-find-guide-card]');

    var cache = {};
    var pushedHistory = false;
    var mode = 'picks';
    var picksSeq = 0;

    var PRICE_MAX = Number(findCfg.priceMax) || 3000;
    var PRICE_STEP = Number(findCfg.step) || 50;
    var CAP = Number(findCfg.cap) || 12;
    /* The top tube slider: which freestyle wheel sizes get it, and its track. */
    var TT_SIZES = (findCfg.ttSizes || []).map(function (s) { return sizeKey(s); }).filter(Boolean);
    var TT_MIN = Number(findCfg.ttMin) || 17;
    var TT_MAX = Number(findCfg.ttMax) || 22;
    var TT_STEP = Number(findCfg.ttStep) || 0.25;
    if (TT_MAX <= TT_MIN) TT_MAX = TT_MIN + 5;

    var find = {
        type: '',
        size: '',
        min: 0,
        max: Infinity,
        ttLo: -Infinity,
        ttHi: Infinity,
        stock: findCfg.stockDefault === true,
        sort: 'popular',
        all: [],
        columns: [],
        supplierMore: 0,
        luxCount: 0,
        shown: CAP,
        feeds: {},
        seq: 0,
        collapsed: false,
        userToggled: false
    };

    /* ---- storage ---------------------------------------------------------- */

    function read() {
        try {
            var raw = localStorage.getItem(KEY);
            if (!raw) return [];
            var list = JSON.parse(raw);
            if (!Array.isArray(list)) return [];
            return list.filter(function (x) { return x && x.handle && x.variant; }).slice(0, config.max);
        } catch (e) {
            return [];
        }
    }

    function write(list) {
        try {
            if (list.length) localStorage.setItem(KEY, JSON.stringify(list));
            else localStorage.removeItem(KEY);
        } catch (e) { /* storage unavailable */ }
    }

    function indexOfVariant(list, variantId) {
        if (!variantId) return -1;
        for (var i = 0; i < list.length; i++) {
            if (String(list[i].variant) === String(variantId)) return i;
        }
        return -1;
    }

    function esc(value) {
        return String(value == null ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    /* ---- glossary popups ---------------------------------------------------- */

    /* The glossary (compare_glossary metaobjects, rendered into the config by
       the section) is a flat list: a term, its other spellings, a short
       explanation, and optionally the rows it is limited to. Entries flagged
       as describing a row name the row rather than a value in it, so they
       stay out of the matching. Nothing is compiled here beyond tidying the
       input; the per-row matcher is built on first use, because which
       entries apply depends on the row. */
    function buildGlossary(list) {
        var entries = [];
        (Array.isArray(list) ? list : []).forEach(function (raw) {
            if (!raw || !raw.t) return;
            var names = [String(raw.t)].concat(Array.isArray(raw.a) ? raw.a : [])
                .map(function (name) { return String(name == null ? '' : name).trim(); })
                .filter(Boolean);
            if (!names.length) return;
            entries.push({
                index: entries.length,
                names: names,
                tip: raw.p ? String(raw.p).trim() : '',
                rows: (Array.isArray(raw.r) ? raw.r : [])
                    .map(function (row) { return String(row == null ? '' : row).trim().toLowerCase(); })
                    .filter(Boolean),
                rowHelp: raw.h === true
            });
        });
        return { entries: entries, matchers: {} };
    }

    function escapeRegExp(text) {
        return text.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&');
    }

    /* One matcher per row. An entry limited to this row beats a general entry
       for the same word. Alternation in a regular expression takes the first
       branch that matches rather than the longest, so the branches are
       ordered by hand: row-specific before general, longer before shorter,
       and the lookup that turns a match back into its entry is filled in the
       same order so the first claim wins. */
    function matcherFor(field) {
        var key = String(field || '').toLowerCase();
        if (glossary.matchers[key]) return glossary.matchers[key];
        var lookup = {};
        var specific = [];
        var general = [];
        glossary.entries.forEach(function (entry) {
            /* Only entries with something to say, about a value rather than a row. */
            if (!entry.tip || entry.rowHelp) return;
            if (entry.rows.length) {
                if (entry.rows.indexOf(key) !== -1) specific.push(entry);
            } else {
                general.push(entry);
            }
        });
        var names = [];
        function claim(entry, rank) {
            entry.names.forEach(function (name) {
                var lower = name.toLowerCase();
                if (lookup[lower]) return;
                lookup[lower] = entry;
                names.push({ name: name, rank: rank });
            });
        }
        specific.forEach(function (entry) { claim(entry, 0); });
        general.forEach(function (entry) { claim(entry, 1); });
        names.sort(function (a, b) {
            if (a.rank !== b.rank) return a.rank - b.rank;
            return b.name.length - a.name.length;
        });
        var matcher = { regex: null, lookup: lookup };
        if (names.length) {
            /* Whole words only, without lookbehind (older Safari has none):
               the character before the term is captured and put back. */
            matcher.regex = new RegExp('(^|[^A-Za-z0-9])(' + names.map(function (n) { return escapeRegExp(n.name); }).join('|') + ')(?![A-Za-z0-9])', 'gi');
        }
        glossary.matchers[key] = matcher;
        return matcher;
    }

    /* A matched word becomes a button that opens the term's popup. The popup
       is one shared element (openTermPop below), so the button only carries
       which entry it is for. */
    function termHtml(word, entry) {
        return '<button type="button" class="lux-cmp__term" data-cmp-term="' + entry.index + '"' +
            ' aria-expanded="false" aria-controls="lux-cmp-pop">' + esc(word) + '</button>';
    }

    /* Escaped HTML for a cell value with its glossary terms marked. Matching
       runs on the raw text and each piece is escaped on the way out, so an
       ampersand in a value neither breaks the pattern nor the page. */
    function linkTerms(value, field) {
        var raw = String(value == null ? '' : value);
        var matcher = matcherFor(field);
        if (!matcher.regex || !raw) return esc(raw);
        var out = '';
        var last = 0;
        var match;
        matcher.regex.lastIndex = 0;
        while ((match = matcher.regex.exec(raw)) !== null) {
            var start = match.index + match[1].length;
            var word = match[2];
            var entry = matcher.lookup[word.toLowerCase()];
            out += esc(raw.slice(last, start));
            out += entry ? termHtml(word, entry) : esc(word);
            last = start + word.length;
            if (!match[0].length) matcher.regex.lastIndex++;
        }
        return out + esc(raw.slice(last));
    }

    /* One popup for the whole table, appended to the body and positioned
       under the term that opened it, or above it near the bottom of the
       screen. A tap or click pins it until the next tap anywhere, Escape, or
       a re-render of the table. With a mouse it also shows on hover and goes
       on mouse out, unless it was clicked. Fixed positioning because the
       panel is a fixed overlay and the table scrolls sideways inside it; a
       scroll anywhere moves the popup with its term. The availability line
       opens the same popup (see availContent below). */
    var POP_SEL = '[data-cmp-term], [data-cmp-avail]';
    var termPop = null;
    var termPopFor = null;
    var termPinned = false;
    var termHoverTimer = null;
    var popTicker = null;
    var hoverable = false;
    try { hoverable = window.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch (e) { hoverable = false; }

    function termEntryOf(button) {
        return glossary.entries[Number(button.getAttribute('data-cmp-term'))] || null;
    }

    function ensureTermPop() {
        if (termPop) return termPop;
        termPop = document.createElement('div');
        termPop.className = 'lux-cmp__pop';
        termPop.id = 'lux-cmp-pop';
        termPop.setAttribute('role', 'tooltip');
        termPop.hidden = true;
        document.body.appendChild(termPop);
        return termPop;
    }

    /* What a popup button has to say: a glossary entry, or the availability
       note. Both come back as a title and a piece of HTML. */
    function popContentFor(button) {
        if (button.hasAttribute('data-cmp-avail')) return availContent(button);
        var entry = termEntryOf(button);
        if (!entry || !entry.tip) return null;
        return { title: entry.names[0], html: esc(entry.tip), ticking: false };
    }

    function openTermPop(button, pinned) {
        var content = popContentFor(button);
        if (!content || !content.html) return;
        var pop = ensureTermPop();
        stopPopTicker();
        if (termPopFor && termPopFor !== button) termPopFor.setAttribute('aria-expanded', 'false');
        pop.innerHTML = (content.title ? '<strong class="lux-cmp__pop-term">' + esc(content.title) + '</strong>' : '') + content.html;
        pop.hidden = false;
        termPopFor = button;
        termPinned = !!pinned;
        button.setAttribute('aria-expanded', 'true');
        placeTermPop();
        if (content.ticking) startPopTicker();
    }

    /* The countdown to the cut-off runs while its popup is open, and the
       popup is rebuilt when the cut-off passes so it stops promising today. */
    function startPopTicker() {
        stopPopTicker();
        popTicker = setInterval(function () {
            var el = termPop && !termPop.hidden ? termPop.querySelector('[data-cmp-countdown]') : null;
            var d = dispatchEngine();
            if (!el || !d || !termPopFor) { stopPopTicker(); return; }
            var left = d.engine.msToCutoff();
            if (left <= 0) { openTermPop(termPopFor, termPinned); return; }
            el.textContent = d.countdownText(left);
        }, 1000);
    }

    function stopPopTicker() {
        if (popTicker) { clearInterval(popTicker); popTicker = null; }
    }

    function placeTermPop() {
        if (!termPop || termPop.hidden || !termPopFor) return;
        if (!document.body.contains(termPopFor)) { closeTermPop(); return; }
        var rect = termPopFor.getBoundingClientRect();
        var vw = window.innerWidth;
        var vh = window.innerHeight;
        var w = termPop.offsetWidth;
        var h = termPop.offsetHeight;
        var gap = 8;
        var left = Math.round(Math.min(Math.max(8, rect.left), Math.max(8, vw - w - 8)));
        var top = Math.round(rect.bottom + gap);
        var above = false;
        if (top + h > vh - 8 && rect.top - gap - h >= 8) {
            top = Math.round(rect.top - gap - h);
            above = true;
        }
        termPop.style.left = left + 'px';
        termPop.style.top = top + 'px';
        termPop.classList.toggle('is-above', above);
        var arrow = Math.round(Math.max(14, Math.min(w - 14, rect.left + rect.width / 2 - left)));
        termPop.style.setProperty('--lux-pop-arrow', arrow + 'px');
    }

    function closeTermPop() {
        if (termHoverTimer) { clearTimeout(termHoverTimer); termHoverTimer = null; }
        stopPopTicker();
        if (!termPop || termPop.hidden) return;
        termPop.hidden = true;
        if (termPopFor) termPopFor.setAttribute('aria-expanded', 'false');
        termPopFor = null;
        termPinned = false;
    }

    function toggleTermPop(button) {
        if (termPop && !termPop.hidden && termPopFor === button && termPinned) { closeTermPop(); return; }
        openTermPop(button, true);
    }

    if (hoverable) {
        document.addEventListener('mouseover', function (e) {
            var term = e.target && e.target.closest ? e.target.closest(POP_SEL) : null;
            if (!term) return;
            if (termHoverTimer) clearTimeout(termHoverTimer);
            if (termPop && !termPop.hidden && termPopFor === term) return;
            termHoverTimer = setTimeout(function () {
                termHoverTimer = null;
                /* A clicked popup stays put while the mouse wanders. */
                if (termPinned) return;
                openTermPop(term, false);
            }, 150);
        });
        document.addEventListener('mouseout', function (e) {
            var term = e.target && e.target.closest ? e.target.closest(POP_SEL) : null;
            if (!term) return;
            var to = e.relatedTarget;
            if (to && to.closest && (to.closest(POP_SEL) === term || to.closest('#lux-cmp-pop'))) return;
            if (termHoverTimer) { clearTimeout(termHoverTimer); termHoverTimer = null; }
            if (!termPinned) closeTermPop();
        });
    }
    document.addEventListener('scroll', function () { if (termPop && !termPop.hidden) placeTermPop(); }, true);
    window.addEventListener('resize', function () { if (termPop && !termPop.hidden) placeTermPop(); });

    /* ---- availability popups ------------------------------------------------ */

    /* The availability line (In stock at LUXBMX, With supplier, Preorder,
       Special order) is a button in the same dotted style as the glossary
       terms, and its popup says when the order would leave. The line's
       markup comes from line-item-availability, the snippet the cart uses,
       so what it says is read back off that markup: the dot names the
       status and the bold part carries the supplier's lead time or the
       preorder's arrival date. The dot stays outside the button so the
       underline does not run under it. */
    var availOn = availCfg.enabled !== false;

    function availInfo(markup) {
        var box = document.createElement('div');
        box.innerHTML = markup;
        var dot = box.querySelector('.line-availability__dot');
        var textEl = dot ? dot.nextElementSibling : null;
        if (!dot || !textEl) return null;
        var lower = textEl.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
        var status = '';
        if (dot.classList.contains('line-availability__dot--lux')) status = 'lux';
        else if (dot.classList.contains('line-availability__dot--pre')) status = 'pre';
        else if (dot.classList.contains('line-availability__dot--supplier')) {
            if (/special order/.test(lower)) status = 'special';
            else if (/with supplier/.test(lower)) status = 'supplier';
        }
        if (!status) return null;
        var strong = textEl.querySelector('strong');
        var detail = (status === 'supplier' || status === 'pre') && strong ? strong.textContent.replace(/\s+/g, ' ').trim() : '';
        var lead = null;
        if (status === 'lux') lead = [0, 0];
        else if (status === 'supplier') lead = leadOf(detail);
        return { status: status, box: box, textEl: textEl, detail: detail, lead: lead };
    }

    /* "Leaves Store Within 1 Business Day" -> [1, 1]; "Leaves Store in 2-5
       Business Days" -> [2, 5]; "In Stock" -> [0, 0]. Anything else (an old
       free-text lead time) has no numbers to estimate from. */
    function leadOf(phrase) {
        var s = String(phrase || '').toLowerCase().trim();
        var m = s.match(/within\s+(\d+)\s+business/);
        if (m) return [Number(m[1]), Number(m[1])];
        m = s.match(/in\s+(\d+)\s*(?:-|to)\s*(\d+)\s+business/);
        if (m) return [Number(m[1]), Number(m[2])];
        if (s === 'in stock') return [0, 0];
        return null;
    }

    function availHtml(markup) {
        if (!availOn) return markup;
        var info = availInfo(markup);
        if (!info || !availCfg[info.status]) return markup;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lux-cmp__term lux-cmp__avail';
        btn.setAttribute('data-cmp-avail', info.status);
        if (info.detail) btn.setAttribute('data-cmp-avail-detail', info.detail);
        if (info.lead) btn.setAttribute('data-cmp-avail-lead', info.lead[0] + '-' + info.lead[1]);
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('aria-controls', 'lux-cmp-pop');
        btn.innerHTML = info.textEl.innerHTML;
        info.textEl.parentNode.replaceChild(btn, info.textEl);
        return info.box.innerHTML;
    }

    /* The site's dispatch engine, shared by custom.js (window.luxDispatch),
       so the popup quotes the same date as the product page and the cart.
       Looked up when first needed rather than at load, and only while the
       estimate is switched on in the theme settings; the cut-off and the
       holiday list are the data attributes on the root, the same ones the
       product page carries. */
    var dispatch = null;

    function dispatchEngine() {
        if (dispatch) return dispatch;
        if (root.dataset.dispatchEnabled !== '1') return null;
        var lib = window.luxDispatch;
        if (!lib || typeof lib.engineFor !== 'function') return null;
        try {
            dispatch = {
                engine: lib.engineFor(root),
                dayFmt: lib.dayFmt,
                countdownText: lib.countdownText,
                wantCountdown: root.dataset.dispatchCountdown === '1'
            };
        } catch (e) {
            return null;
        }
        return dispatch;
    }

    function dayText(d) {
        var fmt = dispatch && dispatch.dayFmt;
        return fmt && typeof fmt.format === 'function' ? fmt.format(d) : d.toDateString();
    }

    /* "13:00" -> "1pm", "13:30" -> "1:30pm". */
    function cutoffText() {
        var m = String(root.dataset.dispatchCutoff || '').match(/^\s*(\d{1,2})\s*:\s*(\d{2})/);
        if (!m) return '';
        var h = Number(m[1]);
        var suffix = h >= 12 ? 'pm' : 'am';
        var hour = h % 12 || 12;
        return hour + (m[2] === '00' ? '' : ':' + m[2]) + suffix;
    }

    /* "Leaves Store Within 1 Business Day" -> "Leaves store within 1 business day." */
    function sentence(text) {
        var s = String(text || '').trim().toLowerCase();
        if (!s) return '';
        return s.charAt(0).toUpperCase() + s.slice(1) + (/[.!?]$/.test(s) ? '' : '.');
    }

    /* The sentence about when the order would leave. With the dispatch
       engine and a lead time it is the product page's estimate, counting
       down to the cut-off while the item can still go today; without them
       it falls back to the words already on the line. */
    function dispatchText(status, lead, detail) {
        var d = dispatchEngine();
        if (d && lead) {
            var est = d.engine.estimate(lead[0], lead[1]);
            if (est.shipsToday && d.wantCountdown) {
                return {
                    html: 'Dispatched <strong>today</strong> if you order within <strong data-cmp-countdown>' + esc(d.countdownText(d.engine.msToCutoff())) + '</strong>.',
                    ticking: true
                };
            }
            if (est.from.getTime() === est.to.getTime()) {
                return { html: 'Estimated dispatch <strong>' + esc(dayText(est.from)) + '</strong>.', ticking: false };
            }
            return { html: 'Estimated dispatch <strong>' + esc(dayText(est.from)) + '</strong> to <strong>' + esc(dayText(est.to)) + '</strong>.', ticking: false };
        }
        if (status === 'lux' && root.dataset.dispatchEnabled === '1' && cutoffText()) {
            return { html: 'Order before <strong>' + esc(cutoffText()) + '</strong> Brisbane time on a business day and it is dispatched the same day.', ticking: false };
        }
        if (status === 'supplier') return { html: esc(sentence(detail)), ticking: false };
        return { html: '', ticking: false };
    }

    function availContent(button) {
        var status = button.getAttribute('data-cmp-avail');
        var template = availCfg[status];
        if (!template) return null;
        var lead = String(button.getAttribute('data-cmp-avail-lead') || '').split('-');
        var detail = button.getAttribute('data-cmp-avail-detail') || '';
        var when = dispatchText(status, lead.length === 2 && lead[0] !== '' ? [Number(lead[0]), Number(lead[1])] : null, detail);
        var html = esc(template)
            .replace('[dispatch]', function () { return when.html; })
            .replace('[date]', function () { return esc(detail || 'soon'); })
            .replace(/\s{2,}/g, ' ').trim();
        return { title: (availCfg.titles || {})[status] || '', html: html, ticking: when.ticking };
    }

    /* ---- buttons ---------------------------------------------------------- */

    /* The button carries the product, but not necessarily the variant being
       looked at. On a product page the buy grid owns the live selection; on a
       tile the swatch the shopper clicked has rewritten the main link. Both are
       read here rather than duplicated into data attributes that would then
       need keeping in sync. */
    function itemFromButton(btn) {
        var item = {
            handle: btn.dataset.cmpHandle,
            url: btn.dataset.cmpUrl,
            title: btn.dataset.cmpTitle,
            image: btn.dataset.cmpImage,
            variant: btn.dataset.cmpVariant,
            variantTitle: btn.dataset.cmpVariantTitle
        };

        /* A quick view drawer sits INSIDE the tile it was opened from, so the
           buy grid has to be tested for first: inside one, its live selection
           is the answer, and the tile's main link is not. */
        var grid = btn.closest('.buy-grid');
        var card = grid ? null : btn.closest('product-item');

        if (grid) {
            var live = grid.currentVariant;
            if (live) {
                item.variant = String(live.id);
                item.variantTitle = (live.options || []).filter(function (o) {
                    return o && o !== 'Default Title';
                }).join(' / ');
            } else if (typeof grid.isIncomplete === 'function' && grid.isIncomplete()) {
                /* No size picked yet. The fallback variant on the button is the
                   first one in stock, and when that happened to be in the tray
                   the button read "Added to compare" on a page where the
                   shopper had chosen nothing - and clicking it only flashed the
                   size labels. Nothing selected means nothing to be in the list. */
                item.variant = null;
                item.variantTitle = '';
            }
            var swatch = grid.querySelector('.buy-grid__swatch.is-selected .buy-grid__swatch-img');
            if (swatch && swatch.currentSrc) item.image = swatch.currentSrc;
            else if (swatch && swatch.src) item.image = swatch.src;
        }

        if (card) {
            var mainLink = card.querySelector('a[data-card-main-link]');
            var fromLink = mainLink ? variantFromHref(mainLink.getAttribute('href')) : null;
            if (fromLink) item.variant = fromLink;
            var parts = [];
            card.querySelectorAll('a[data-card-swatch].is-active, a.card-option-pill.is-active').forEach(function (a) {
                if (a.dataset.optionValue) parts.push(a.dataset.optionValue);
            });
            if (parts.length) item.variantTitle = parts.join(' / ');
            /* Tiles with more than one photo carry a swipeable track instead
               of hidden-image swapping. The photo to file is the chosen
               colour's, not whichever one happens to be swiped into view -
               so the active swatch's media id is looked up first, then the
               slide on screen, then the single-image tile's plain <img>. */
            var activeSwatch = card.querySelector('a[data-card-swatch].is-active');
            var img = null;
            if (activeSwatch && activeSwatch.dataset.mediaId) {
                img = card.querySelector('.lux-tile-media__slide[data-media-id="' + activeSwatch.dataset.mediaId + '"] img');
            }
            if (!img) img = card.querySelector('.lux-tile-media__slide.is-active img');
            if (!img) img = card.querySelector('.product-item__primary-image:not([hidden])');
            /* A slide that has not been swiped to is lazy and has no
               currentSrc yet; its src attribute is still a real URL. */
            if (img) item.image = img.currentSrc || img.getAttribute('src') || item.image;
        }

        return item;
    }

    function variantFromHref(href) {
        if (!href) return null;
        var match = href.match(/[?&]variant=(\d+)/);
        return match ? match[1] : null;
    }

    /* Which variant a button currently offers, so its state answers "is THIS
       bike in the list" rather than "is anything from this product in it". */
    function buttonVariant(btn) {
        return itemFromButton(btn).variant;
    }

    function syncButtons() {
        var list = read();
        document.querySelectorAll('[data-cmp-add]').forEach(function (btn) {
            var inList = indexOfVariant(list, buttonVariant(btn)) !== -1;
            btn.classList.toggle('is-in', inList);
            btn.setAttribute('aria-pressed', String(inList));
            var label = btn.querySelector('[data-cmp-add-label]');
            if (!label) return;
            if (!label.dataset.cmpDefault) label.dataset.cmpDefault = label.textContent;
            label.textContent = inList ? (btn.dataset.cmpInLabel || 'Added to compare') : label.dataset.cmpDefault;
        });
    }

    function toggleFromButton(btn) {
        var grid = btn.closest('.buy-grid');
        /* Nothing useful can be compared until the shopper has picked a size,
           so borrow the buy grid's own nudge rather than guessing a variant. */
        if (grid && typeof grid.isIncomplete === 'function' && grid.isIncomplete()) {
            if (typeof grid.flashSelectLabels === 'function') grid.flashSelectLabels();
            return;
        }

        var item = itemFromButton(btn);
        if (!item.handle || !item.variant) return;

        var list = read();
        var at = indexOfVariant(list, item.variant);
        if (at !== -1) {
            list.splice(at, 1);
            write(list);
            renderTray();
            syncButtons();
            return;
        }

        if (list.length >= config.max) {
            showHint(labels.full || 'Remove one to add another.');
            openTray();
            return;
        }

        list.push(item);
        write(list);
        renderTray();
        syncButtons();
        openTray();
        refine(item);
    }

    /* A tile knows the variant id (its main link carries it) but not always the
       whole variant name: with a colour clicked and no size, the tile can only
       offer "Black/White" for a variant that is "Black/White / 20.5", and a
       tray that hides the size is a tray you cannot check. The feed knows, so
       the name and image are corrected from it once it lands. The fetch is the
       same one the table needs, so it also warms the cache for opening it. */
    function refine(item) {
        fetchProduct(item).then(function (data) {
            var variant = variantOf(data, item);
            if (!variant) return;
            var list = read();
            var at = indexOfVariant(list, item.variant);
            if (at === -1) return;
            var name = (variant.options || []).filter(function (o) {
                return o && o !== 'Default Title';
            }).join(' / ');
            if (name) list[at].variantTitle = name;
            if (!list[at].image && variant.image) list[at].image = variant.image;
            write(list);
            renderTray();
        }, function () { /* the table will report the failure when it opens */ });
    }

    /* ---- tray ------------------------------------------------------------- */

    var hintTimer = null;

    function showHint(text) {
        if (!hintEl) return;
        hintEl.textContent = text;
        hintEl.hidden = false;
        clearTimeout(hintTimer);
        hintTimer = setTimeout(function () { renderTray(); }, 4000);
    }

    function collapsed() {
        try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch (e) { return false; }
    }

    function openTray() {
        if (!collapseBtn) return;
        try { localStorage.removeItem(COLLAPSE_KEY); } catch (e) { /* storage unavailable */ }
        collapseBtn.setAttribute('aria-expanded', 'true');
    }

    function pillDismissed() {
        try { return sessionStorage.getItem(PILL_KEY) === '1'; } catch (e) { return false; }
    }

    /* The pill offers the finder while the tray is empty, in the tray's own
       corner, so the two are never on screen together. */
    function renderPill(list) {
        if (!pill) return false;
        var show = findOn && !list.length && !pillDismissed();
        pill.hidden = !show;
        return show;
    }

    function renderTray() {
        var list = read();
        if (tabCount) tabCount.textContent = list.length;
        if (!tray) return;

        if (!list.length) {
            tray.hidden = true;
            setBodyOffset(renderPill(list) ? pill.offsetHeight : 0);
            return;
        }

        renderPill(list);
        tray.hidden = false;
        if (collapseBtn) collapseBtn.setAttribute('aria-expanded', String(!collapsed()));
        if (countEl) countEl.textContent = list.length + ' of ' + config.max;

        var html = '';
        list.forEach(function (item) {
            html += '<li class="lux-cmp__item">';
            if (item.image) {
                html += '<img class="lux-cmp__thumb" src="' + esc(item.image) + '" alt="" loading="lazy" width="44" height="44">';
            } else {
                html += '<span class="lux-cmp__thumb"></span>';
            }
            html += '<span class="lux-cmp__item-name">' + esc(item.title);
            if (item.variantTitle) html += '<span class="lux-cmp__item-variant">' + esc(item.variantTitle) + '</span>';
            html += '</span>';
            html += '<button type="button" class="lux-cmp__remove" data-cmp-remove="' + esc(item.variant) +
                '" aria-label="Remove ' + esc(item.title) + ' from compare">' +
                '<svg focusable="false" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">' +
                '<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"></path>' +
                '</svg></button>';
            html += '</li>';
        });
        listEl.innerHTML = html;

        if (hintEl) {
            if (list.length < 2) {
                hintEl.textContent = labels.one || 'Add one more bike to compare.';
                hintEl.hidden = false;
            } else {
                hintEl.hidden = true;
                hintEl.textContent = '';
            }
        }

        if (openBtn) openBtn.hidden = list.length < 2;
        if (openLabel) {
            var template = labels.open || 'Compare [count] bikes';
            var openText = template.replace('[count]', list.length);
            /* The button is hidden below two, so this only matters if the
               template is ever shown for one; cheap insurance against
               "Compare 1 bikes". */
            if (list.length === 1) openText = openText.replace(/\bbikes\b/i, 'bike');
            openLabel.textContent = openText;
        }

        setBodyOffset(tray.offsetHeight);
    }

    /* The footer is the one thing a fixed tray can permanently bury, so it is
       padded by whatever the tray is actually tall - the height changes with
       the number of bikes and whether it is collapsed. */
    function setBodyOffset(height) {
        document.documentElement.style.setProperty('--lux-cmp-h', height ? height + 'px' : '0px');
        document.body.classList.toggle('lux-cmp-tray-open', height > 0);
    }

    /* ---- panel ------------------------------------------------------------ */

    function fetchProduct(item) {
        var url = item.url || '/products/' + item.handle;
        if (cache[url]) return cache[url];
        var joiner = url.indexOf('?') === -1 ? '?' : '&';
        /* No Accept: application/json header. Asking a product URL for JSON
           gets Shopify's own /products/x.js payload and the ?view= is ignored,
           so the fetch quietly returns a product with no metafields on it. */
        cache[url] = fetch(url + joiner + 'view=compare')
            .then(function (res) {
                if (!res.ok) throw new Error('compare fetch failed: ' + res.status);
                return res.json();
            })
            .catch(function (err) {
                delete cache[url];
                throw err;
            });
        return cache[url];
    }

    function hashFor(which) {
        return which === 'find' ? '#find-bikes' : '#compare';
    }

    function isPanelHash(hash) {
        return hash === '#compare' || hash === '#find-bikes';
    }

    /* The panel's chrome follows the tab: the filter bar and status line only
       exist for Find bikes, the tools row lays out differently, and the panel
       takes a fixed height on a desktop so the table does not resize under
       the filters as they change. */
    function applyMode() {
        panel.classList.toggle('is-find', mode === 'find');
        if (findBar) findBar.hidden = mode !== 'find';
        if (findStatus) findStatus.hidden = mode !== 'find';
        if (sortWrap) sortWrap.hidden = mode !== 'find';
        if (toolsEl) toolsEl.classList.toggle('is-find', mode === 'find');
        tabs.forEach(function (tab) {
            tab.setAttribute('aria-selected', String(tab.dataset.cmpTab === mode));
        });
        if (window.innerWidth >= 1000 && find.collapsed) setCollapsed(false);
    }

    function openPanel(which) {
        var list = read();
        if (which !== 'find' && which !== 'picks') which = 'picks';
        if (which === 'find' && !findOn) which = 'picks';
        /* Without the finder, the panel has nothing to say about fewer than
           two picks and stays shut, as it always did. With it, the picks tab
           shows the nudge and the Find tab is a tap away. */
        if (which === 'picks' && list.length < 2 && !findOn) return;
        mode = which;

        panel.hidden = false;
        scrim.hidden = false;
        document.body.classList.add('lux-cmp-panel-open');
        if (diffInput) diffInput.checked = differencesOnly();
        applyMode();

        /* A history entry so the phone's back gesture closes the table instead
           of leaving the page underneath it. Only when this open is what put
           the hash in the URL: a shopper landing on a shared #compare link has
           nothing behind it, and closing must not walk them off the site. */
        try {
            if (!isPanelHash(window.location.hash)) {
                history.pushState({ luxCompare: true }, '', hashFor(mode));
                pushedHistory = true;
            } else if (window.location.hash !== hashFor(mode)) {
                history.replaceState({ luxCompare: true }, '', hashFor(mode));
            }
        } catch (e) { /* history unavailable */ }

        if (mode === 'find') renderFind();
        else renderPicks();
    }

    function switchMode(which) {
        if (which === mode) return;
        mode = which;
        applyMode();
        try {
            if (isPanelHash(window.location.hash) && window.location.hash !== hashFor(mode)) {
                history.replaceState({ luxCompare: true }, '', hashFor(mode));
            }
        } catch (e) { /* history unavailable */ }
        if (mode === 'find') renderFind();
        else renderPicks();
    }

    function closePanel(fromHistory) {
        panel.hidden = true;
        scrim.hidden = true;
        closeGuide();
        closeTermPop();
        document.body.classList.remove('lux-cmp-panel-open');
        if (!fromHistory && isPanelHash(window.location.hash)) {
            try {
                if (pushedHistory) history.back();
                else history.replaceState({}, '', window.location.pathname + window.location.search);
            } catch (e) { /* history unavailable */ }
        }
        pushedHistory = false;
    }

    function differencesOnly() {
        try {
            var stored = localStorage.getItem(DIFF_KEY);
            if (stored === '1') return true;
            if (stored === '0') return false;
        } catch (e) { /* storage unavailable */ }
        return config.differencesDefault === true;
    }

    function setDifferencesOnly(state) {
        try { localStorage.setItem(DIFF_KEY, state ? '1' : '0'); } catch (e) { /* storage unavailable */ }
    }

    /* No falling back to the first variant. A variant that has been deleted or
       renumbered since it was added must read as missing, because the one thing
       worse than an empty column is a full one describing a different bike. */
    function variantOf(data, item) {
        var variants = (data && data.variants) || [];
        for (var i = 0; i < variants.length; i++) {
            if (String(variants[i].id) === String(item.variant)) return variants[i];
        }
        return null;
    }

    /* Every row resolves to { html, text }: html is what the cell shows, text
       is what the differences filter compares, so an availability cell with
       markup in it is still matched on its wording. */
    function cellFor(row, column) {
        var data = column.data;
        var variant = column.variant;
        var empty = { html: '<span class="lux-cmp__dash">-</span>', text: '' };
        if (!data || !variant) return empty;

        if (row.types && row.types.length) {
            var type = String(data.type || '').toLowerCase();
            if (row.types.indexOf(type) === -1) return empty;
        }

        var field = row.field;

        if (field === 'price') {
            var price = '<span class="lux-cmp__price bold">' + esc(variant.price) + '</span>';
            if (data.currency) price += ' <span class="lux-cmp__cur">' + esc(data.currency) + '</span>';
            if (variant.compare_at) price += '<span class="lux-cmp__was">' + esc(variant.compare_at) + '</span>';
            return { html: price, text: String(variant.price_cents) };
        }

        if (field === 'availability') {
            var markup = variant.availability_html || '';
            /* The line becomes a popup button (availHtml); the plain words
               are what the differences filter compares. */
            return { html: availHtml(markup), text: markup.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() };
        }

        if (field === 'sku') {
            if (!variant.sku) return empty;
            return { html: esc(variant.sku), text: variant.sku };
        }

        if (field.indexOf('option:') === 0) {
            var wanted = field.slice(7).trim().toLowerCase();
            var names = data.option_names || [];
            for (var i = 0; i < names.length; i++) {
                if (String(names[i]).trim().toLowerCase() === wanted) {
                    var value = (variant.options || [])[i];
                    if (!value || value === 'Default Title') return empty;
                    return { html: esc(value), text: value };
                }
            }
            return empty;
        }

        var own = variant.fields && variant.fields[field];
        var shared = data.fields && data.fields[field];
        var resolved = own || shared;
        if (!resolved) return empty;
        /* Glossary terms in the value get their popup buttons; the plain text
           is what the differences filter compares. */
        return { html: linkTerms(resolved, field), text: String(resolved) };
    }

    function gripHtml(title) {
        return '<button type="button" class="lux-cmp__col-grip" data-cmp-grip aria-label="Move ' + esc(title) +
            '. Drag it, or press the left and right arrow keys">' +
            '<svg focusable="false" width="10" height="16" viewBox="0 0 10 16" aria-hidden="true">' +
            '<circle cx="2.5" cy="3" r="1.5" fill="currentColor"></circle><circle cx="7.5" cy="3" r="1.5" fill="currentColor"></circle>' +
            '<circle cx="2.5" cy="8" r="1.5" fill="currentColor"></circle><circle cx="7.5" cy="8" r="1.5" fill="currentColor"></circle>' +
            '<circle cx="2.5" cy="13" r="1.5" fill="currentColor"></circle><circle cx="7.5" cy="13" r="1.5" fill="currentColor"></circle>' +
            '</svg></button>';
    }

    /* Every table write comes through here so the sideways arrows can be
       shown or hidden to match what is in the panel. */
    function setTableHtml(html) {
        tableEl.innerHTML = html;
        updateScrollNav();
    }

    /* The arrows at the end of the tools row scroll the bikes sideways a
       screen at a time, for a mouse with no sideways wheel. Shown only while
       some of the bikes are off screen, and greyed at either end. */
    function updateScrollNav() {
        if (!scrollNav || !tableEl) return;
        var table = tableEl.querySelector('.lux-cmp__table');
        var can = !!table && tableEl.scrollWidth > tableEl.clientWidth + 1;
        scrollNav.hidden = !can;
        if (!can) return;
        var left = Math.round(tableEl.scrollLeft);
        if (scrollPrev) scrollPrev.disabled = left <= 0;
        if (scrollNext) scrollNext.disabled = left + tableEl.clientWidth >= tableEl.scrollWidth - 1;
    }

    /* The scroll is animated, so a second click before the first has landed
       is measured from where the first is heading, not from wherever the
       animation has got to: two quick clicks move two screens. */
    var scrollGoal = null;
    var scrollGoalTimer = null;

    function scrollColumns(dir) {
        var corner = tableEl.querySelector('.lux-cmp__corner');
        var labelWidth = corner ? corner.getBoundingClientRect().width : 0;
        var step = Math.max(160, tableEl.clientWidth - labelWidth - 40);
        var from = scrollGoal === null ? tableEl.scrollLeft : scrollGoal;
        var most = Math.max(0, tableEl.scrollWidth - tableEl.clientWidth);
        var to = Math.max(0, Math.min(most, from + dir * step));
        scrollGoal = to;
        clearTimeout(scrollGoalTimer);
        scrollGoalTimer = setTimeout(function () { scrollGoal = null; }, 700);
        try {
            tableEl.scrollTo({ left: to, behavior: 'smooth' });
        } catch (e) {
            tableEl.scrollLeft = to;
        }
    }

    tableEl.addEventListener('scroll', function () {
        if (scrollGoal !== null && Math.abs(tableEl.scrollLeft - scrollGoal) < 1) scrollGoal = null;
        updateScrollNav();
    }, { passive: true });

    /* Your picks: the shopper's list, in the order they dragged it into.
       Find bikes: whatever the filters produced, with a tick per column that
       is the product tile's compare button wearing different clothes - same
       data attributes, same handler, same state sync. */
    function renderTable(columns, isFind) {
        closeTermPop();
        columns.forEach(function (column) {
            if (!isFind) column.variant = variantOf(column.data, column.item);
        });

        var diff = differencesOnly();
        /* The corner has no row label and no bike, so it carries the picture
           the section is given, cropped to the cell. */
        var corner = config.cornerHtml || '';
        var head = '<thead><tr><td class="lux-cmp__corner' + (corner ? ' lux-cmp__corner--img' : '') + '">' + corner + '</td>';

        columns.forEach(function (column, index) {
            var item = column.item;
            var variant = column.variant;
            var url = variant ? variant.url : (item.url || '/products/' + item.handle);
            var image = (variant && variant.image) || item.image;
            head += '<th scope="col" class="lux-cmp__col-head" data-cmp-col="' + index + '">';
            head += gripHtml(item.title);
            if (isFind) {
                head += '<button type="button" class="lux-cmp__col-add" data-cmp-add' +
                    ' data-cmp-handle="' + esc(item.handle) + '"' +
                    ' data-cmp-url="' + esc(item.url) + '"' +
                    ' data-cmp-title="' + esc(item.title) + '"' +
                    ' data-cmp-image="' + esc(image || '') + '"' +
                    ' data-cmp-variant="' + esc(item.variant) + '"' +
                    ' data-cmp-variant-title="' + esc(item.variantTitle || '') + '"' +
                    ' data-cmp-in-label="' + esc(labels.compareIn || 'In your picks') + '"' +
                    ' aria-pressed="false">' +
                    '<span class="lux-cmp-add__box" aria-hidden="true">' +
                    '<svg focusable="false" width="10" height="10" viewBox="0 0 12 12"><path d="M1.5 6.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>' +
                    '</span><span data-cmp-add-label>' + esc(labels.compare || 'Compare') + '</span></button>';
            } else {
                head += '<button type="button" class="lux-cmp__col-remove" data-cmp-remove="' + esc(item.variant) +
                    '" aria-label="Remove ' + esc(item.title) + ' from compare">' +
                    '<svg focusable="false" width="12" height="12" viewBox="0 0 14 14" aria-hidden="true">' +
                    '<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"></path>' +
                    '</svg></button>';
            }
            head += '<a href="' + esc(url) + '">';
            if (image) head += '<img class="lux-cmp__col-img" src="' + esc(image) + '" alt="" loading="lazy">';
            head += '<span class="lux-cmp__col-title">' + esc(item.title) + '</span>';
            var variantName = item.variantTitle || (variant && variant.title) || '';
            if (variantName && variantName !== 'Default Title') {
                head += '<span class="lux-cmp__col-variant">' + esc(variantName) + '</span>';
            }
            head += '</a>';
            if (!isFind) {
                if (!column.data) head += '<span class="lux-cmp__col-note">Details could not be loaded</span>';
                else if (!variant) head += '<span class="lux-cmp__col-note">This size is no longer available</span>';
            }
            head += '</th>';
        });
        head += '</tr></thead>';

        var body = '<tbody>';
        (config.rows || []).forEach(function (row) {
            var cells = columns.map(function (column) { return cellFor(row, column); });
            var filled = cells.filter(function (cell) { return cell.text !== ''; });
            /* A row no bike has a value for is noise whatever the toggle says. */
            if (!filled.length) return;
            if (diff) {
                var same = cells.every(function (cell) { return cell.text === cells[0].text; });
                if (same) return;
            }
            body += '<tr><th scope="row">' + esc(row.label) + '</th>';
            cells.forEach(function (cell) { body += '<td>' + cell.html + '</td>'; });
            body += '</tr>';
        });
        body += '</tbody>';

        var foot = '<tfoot><tr><td class="lux-cmp__corner"></td>';
        columns.forEach(function (column) {
            var variant = column.variant;
            var url = variant ? variant.url : (column.item.url || '/products/' + column.item.handle);
            foot += '<td><a class="lux-cmp__choose button button--primary button--small tw-rounded-[4px]" href="' +
                esc(url) + '">' + esc(labels.choose || 'Choose') + '</a></td>';
        });
        foot += '</tr></tfoot>';

        var extra = '';
        if (isFind && find.all.length > columns.length) {
            var remaining = find.all.length - columns.length;
            extra = '<div class="lux-cmp__find-more"><button type="button" class="button button--primary button--small tw-rounded-[4px]" data-find-more>' +
                '<span class="text--xsmall bold">Show ' + Math.min(CAP, remaining) + ' more</span></button></div>';
        }

        setTableHtml('<table class="lux-cmp__table">' + head + body + foot + '</table>' + extra);
        /* The CSS builds the table's minimum width from this: equal columns
           that scroll rather than squash, whether there are two bikes or four. */
        tableEl.querySelector('.lux-cmp__table').style.setProperty('--lux-cmp-cols', columns.length);

        /* Two bikes are already side by side; the handle only earns an
           explanation once there is somewhere to move a column to. */
        if (reorderHint) {
            reorderHint.textContent = labels.reorder || '';
            reorderHint.hidden = columns.length < 3 || !reorderHint.textContent;
        }

        if (isFind) syncButtons();
    }

    function emptyHtml(text, extra) {
        return '<p class="lux-cmp__empty">' + text + (extra ? ' ' + extra : '') + '</p>';
    }

    function renderPicks() {
        var list = read();
        if (reorderHint) reorderHint.hidden = true;
        if (list.length < 2) {
            var nudge = findOn ? '<button type="button" data-cmp-find>' + esc(labels.findLink || 'Find bikes by size & budget') + '</button>' : '';
            setTableHtml(emptyHtml(esc(list.length ? (labels.one || 'Add one more bike to compare.') : (labels.empty || 'Add a bike to compare it here.')), nudge));
            return;
        }
        setTableHtml('<p class="lux-cmp__empty">Loading</p>');
        var seq = ++picksSeq;
        Promise.all(list.map(function (item) {
            return fetchProduct(item).then(function (data) {
                return { item: item, data: data };
            }, function () {
                return { item: item, data: null };
            });
        })).then(function (columns) {
            if (seq !== picksSeq || mode !== 'picks' || panel.hidden) return;
            renderTable(columns, false);
        });
    }

    /* ---- reordering ------------------------------------------------------- */

    /* Columns are the order of the stored list, so moving one is a splice and
       a re-render; the tray follows because it renders from the same list.
       The table is rebuilt from cache, so it is instant. In Find bikes the
       order is the result set's, and dragging reorders that instead - it
       resets when the filters produce a new set. */
    function moveColumn(from, to, focusGrip) {
        if (mode === 'find') {
            var cols = find.columns;
            if (from === to || from < 0 || to < 0 || from >= cols.length || to >= cols.length) return;
            var movedCol = cols.splice(from, 1)[0];
            cols.splice(to, 0, movedCol);
            /* Keep the full set in step, so Show more appends after the
               shopper's arrangement rather than shuffling it. */
            find.all = cols.concat(find.all.slice(cols.length));
            renderTable(cols, true);
            if (focusGrip) {
                var g = tableEl.querySelector('th[data-cmp-col="' + to + '"] [data-cmp-grip]');
                if (g) g.focus();
            }
            return;
        }

        var list = read();
        if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return;
        var moved = list.splice(from, 1)[0];
        list.splice(to, 0, moved);
        write(list);
        renderTray();
        Promise.all(list.map(function (item) {
            return fetchProduct(item).then(function (data) {
                return { item: item, data: data };
            }, function () {
                return { item: item, data: null };
            });
        })).then(function (columns) {
            renderTable(columns, false);
            if (focusGrip) {
                var grip = tableEl.querySelector('th[data-cmp-col="' + to + '"] [data-cmp-grip]');
                if (grip) grip.focus();
            }
        });
    }

    /* A column is one cell in every row, so marking it means marking the
       nth child of each row. The corner cell is child 1. */
    function markColumn(index, className, on) {
        if (index < 0) return;
        tableEl.querySelectorAll('.lux-cmp__table tr > :nth-child(' + (index + 2) + ')').forEach(function (cell) {
            cell.classList.toggle(className, on);
        });
    }

    /* Pointer events rather than HTML drag and drop, which does nothing on a
       touch screen, and that is where this table mostly lives. The grip has
       touch-action: none, so a finger on it drags the column while a finger
       anywhere else still scrolls the table. */
    var drag = null;
    var dragFrame = null;

    function columnUnderPointer(clientX) {
        var heads = tableEl.querySelectorAll('th[scope="col"]');
        var found = -1;
        heads.forEach(function (head, i) {
            var rect = head.getBoundingClientRect();
            if (clientX >= rect.left && clientX <= rect.right) found = i;
            if (found === -1 && i === heads.length - 1 && clientX > rect.right) found = i;
            if (i === 0 && clientX < rect.left) found = 0;
        });
        return found;
    }

    /* Nudge the table sideways while the pointer is held near either edge, so
       a column can be dragged to one that is off screen. Runs per frame only
       while a drag is live. */
    function autoScroll() {
        if (!drag || !drag.active) { dragFrame = null; return; }
        var body = root.querySelector('.lux-cmp__panel-body');
        var rect = body.getBoundingClientRect();
        var edge = 48;
        if (drag.clientX > rect.right - edge) body.scrollLeft += Math.min(12, (drag.clientX - (rect.right - edge)) / 3);
        else if (drag.clientX < rect.left + edge) body.scrollLeft -= Math.min(12, ((rect.left + edge) - drag.clientX) / 3);
        var over = columnUnderPointer(drag.clientX);
        if (over !== -1 && over !== drag.to) {
            markColumn(drag.to, 'is-drop-target', false);
            drag.to = over;
            markColumn(over, 'is-drop-target', over !== drag.from);
        }
        dragFrame = requestAnimationFrame(autoScroll);
    }

    function endDrag() {
        if (!drag) return;
        var done = drag;
        drag = null;
        if (dragFrame) { cancelAnimationFrame(dragFrame); dragFrame = null; }
        markColumn(done.from, 'is-dragging', false);
        markColumn(done.to, 'is-drop-target', false);
        document.body.classList.remove('lux-cmp-dragging');
        if (done.active && done.to !== -1 && done.to !== done.from) moveColumn(done.from, done.to, false);
    }

    tableEl.addEventListener('pointerdown', function (e) {
        var grip = e.target.closest ? e.target.closest('[data-cmp-grip]') : null;
        if (!grip) return;
        var head = grip.closest('th[data-cmp-col]');
        if (!head) return;
        drag = { from: Number(head.dataset.cmpCol), to: Number(head.dataset.cmpCol), startX: e.clientX, clientX: e.clientX, active: false };
        try { grip.setPointerCapture(e.pointerId); } catch (err) { /* capture unavailable */ }
        e.preventDefault();
    });

    tableEl.addEventListener('pointermove', function (e) {
        if (!drag) return;
        drag.clientX = e.clientX;
        if (!drag.active) {
            if (Math.abs(e.clientX - drag.startX) < 6) return;
            drag.active = true;
            markColumn(drag.from, 'is-dragging', true);
            document.body.classList.add('lux-cmp-dragging');
            if (!dragFrame) dragFrame = requestAnimationFrame(autoScroll);
        }
    });

    tableEl.addEventListener('pointerup', endDrag);
    tableEl.addEventListener('pointercancel', endDrag);

    tableEl.addEventListener('keydown', function (e) {
        var grip = e.target.closest ? e.target.closest('[data-cmp-grip]') : null;
        if (!grip) return;
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        var from = Number(grip.closest('th[data-cmp-col]').dataset.cmpCol);
        moveColumn(from, e.key === 'ArrowLeft' ? from - 1 : from + 1, true);
    });

    /* ---- Find bikes: data ------------------------------------------------- */

    /* The feed is asked for in "most popular" order - Shopify's best sellers
       unless the section says to trust the collection's own order - and the
       position each product lands at is kept as its rank. Every other sort
       is done here from that one fetch. */
    var FEED_SORT = String(findCfg.feedSort || 'best-selling');

    function feedUrl(handle, page) {
        var url = '/collections/' + encodeURIComponent(handle) + '?view=compare';
        if (FEED_SORT && FEED_SORT !== 'default') url += '&sort_by=' + encodeURIComponent(FEED_SORT);
        return url + (page > 1 ? '&page=' + page : '');
    }

    /* One fetch per category per page load, every page of it, cached as the
       promise so a second look at the same size is instant. Same no-cache
       across page loads as the picks: stock moves. */
    function loadFeed(handle) {
        if (find.feeds[handle]) return find.feeds[handle];
        find.feeds[handle] = fetch(feedUrl(handle, 1))
            .then(function (res) {
                if (!res.ok) throw new Error('find fetch failed: ' + res.status);
                return res.json();
            })
            .then(function (first) {
                var products = first.products || [];
                var pages = Number(first.pages) || 1;
                var rest = [];
                for (var p = 2; p <= pages; p++) {
                    rest.push(fetch(feedUrl(handle, p)).then(function (res) {
                        if (!res.ok) throw new Error('find fetch failed: ' + res.status);
                        return res.json();
                    }));
                }
                return Promise.all(rest).then(function (more) {
                    more.forEach(function (d) { products = products.concat(d.products || []); });
                    products.forEach(function (product, i) {
                        product.currency = first.currency;
                        product.rank = i;
                    });
                    return products;
                });
            })
            .catch(function (err) {
                delete find.feeds[handle];
                throw err;
            });
        return find.feeds[handle];
    }

    function handleFor(type, size) {
        if (type === 'race') return findCfg.raceHandle || 'bmx-race-bikes';
        var pattern = findCfg.sizeHandle || '[size]-inch-bmx-bikes';
        return pattern.replace('[size]', String(size).replace('.', ''));
    }

    function fieldOf(product, key) {
        var f = product.fields || {};
        if (f[key]) return String(f[key]);
        var variants = product.variants || [];
        for (var i = 0; i < variants.length; i++) {
            if (variants[i].fields && variants[i].fields[key]) return String(variants[i].fields[key]);
        }
        return '';
    }

    function disciplineOf(product) {
        return fieldOf(product, 'custom.discipline').toLowerCase();
    }

    function isRace(product) {
        return /race/.test(disciplineOf(product));
    }

    function isFreestyle(product) {
        var d = disciplineOf(product);
        return /freestyle/.test(d) || !/race/.test(d);
    }

    function wheelOf(product) {
        var w = fieldOf(product, 'custom.wheel_size') || fieldOf(product, 'global.wheel_size');
        var m = w.match(/(\d+(?:\.\d+)?)/);
        return m ? m[1] : '';
    }

    /* Race frames are sized by class and the class is in the name, with each
       brand spelling it its own way: "Pro XL", "Pro Bike XL", "Pro L" (a Pro),
       "Cruiser XL" (a Cruiser). Longest names first so "Pro XXL" is not read
       as "Pro". A 20 inch race bike with no class in its name falls back to
       the top tube, banded as the race size guide has it; anything on another
       wheel size without a class is not a race frame (the race collection
       carries a 12 inch balance bike tagged Race) and gets no class at all. */
    var CLASS_RULES = [
        ['Pro XXXXL', /\bpro(?:\s+bike)?\s*xxxxl\b/i],
        ['Pro XXXL', /\bpro(?:\s+bike)?\s*xxxl\b/i],
        ['Pro XXL', /\bpro(?:\s+bike)?\s*xxl\b/i],
        ['Pro XL', /\bpro(?:\s+bike)?\s*xl\b/i],
        ['Pro', /\bpro(?:\s+bike)?\s*l\b|\bpro\b/i],
        ['Expert XL', /\bexpert(?:\s+bike)?\s*xl\b/i],
        ['Expert', /\bexpert\b/i],
        ['Junior', /\bjunior\b/i],
        ['Micro', /\bmicro\b/i],
        ['Mini', /\bmini\b/i]
    ];

    function raceClassOf(product) {
        var title = String(product.title || '');
        if (/\bcruiser\b/i.test(title) || wheelOf(product) === '24') return 'Cruiser';
        for (var i = 0; i < CLASS_RULES.length; i++) {
            if (CLASS_RULES[i][1].test(title)) return CLASS_RULES[i][0];
        }
        var wheel = wheelOf(product);
        if (wheel && wheel !== '20') return '';
        var tt = parseFloat(fieldOf(product, 'global.top_tube_length'));
        if (!isFinite(tt)) return '';
        if (tt <= 16.5) return 'Micro';
        if (tt <= 18.1) return 'Mini';
        if (tt <= 18.9) return 'Junior';
        if (tt <= 19.9) return 'Expert';
        if (tt <= 20.2) return 'Expert XL';
        if (tt <= 20.9) return 'Pro';
        if (tt <= 21.3) return 'Pro XL';
        if (tt <= 21.9) return 'Pro XXL';
        if (tt <= 22.2) return 'Pro XXXL';
        return 'Pro XXXXL';
    }

    function sizeKey(s) {
        return String(s || '').replace(/["”″]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
    }

    function inCategory(product) {
        if (find.type === 'race') {
            var d = disciplineOf(product);
            if (d && !/race/.test(d)) return false;
            return sizeKey(raceClassOf(product)) === sizeKey(find.size);
        }
        return isFreestyle(product);
    }

    function variantName(variant) {
        return (variant.options || []).filter(function (o) { return o && o !== 'Default Title'; }).join(' / ');
    }

    /* Every colour and size that fits becomes a column, so the table compares
       the bikes a shopper could actually buy: a $599 blue and a $699 ocean
       blue are two rows in the shop and two columns here. The stock switch
       counts the units at the LUXBMX location, which is what the buy grid and
       cart mean by "in stock"; everything else the store can still sell -
       supplier feed, preorder, special order - is counted so the status line
       can say how many the switch is hiding. */
    function computeFind(products) {
        var cols = [];
        var supplierMore = 0;
        var luxCount = 0;
        var minCents = find.min * 100;
        var maxCents = isFinite(find.max) ? find.max * 100 : Infinity;
        var ttOn = ttActive() && (isFinite(find.ttLo) || isFinite(find.ttHi));
        products.forEach(function (product) {
            if (!inCategory(product)) return;
            (product.variants || []).forEach(function (variant, vi) {
                if (variant.available === false) return;
                var cents = Number(variant.price_cents) || 0;
                if (cents < minCents || cents > maxCents) return;
                /* A bike with no top tube on record cannot be shown to fit a
                   narrowed range, so it drops out until the range is opened
                   up again; with the slider at both ends everything stays. */
                if (ttOn) {
                    var tt = topTubeOf(product, variant);
                    if (!isFinite(tt) || tt < find.ttLo || tt > find.ttHi) return;
                }
                var lux = Number(variant.lux_qty) > 0;
                if (find.stock && !lux) { supplierMore++; return; }
                if (lux) luxCount++;
                var name = variantName(variant);
                cols.push({
                    item: {
                        handle: product.handle,
                        url: variant.url || product.url,
                        title: product.title,
                        image: variant.image || product.image,
                        variant: String(variant.id),
                        variantTitle: name
                    },
                    data: product,
                    variant: variant,
                    lux: lux,
                    order: vi
                });
            });
        });
        cols.sort(SORTS[sortKey()]);
        return { cols: cols, supplierMore: supplierMore, luxCount: luxCount };
    }

    /* The orders on offer. Popular is the feed's own order (see feedUrl),
       with a product's colours and sizes kept in the order the shop lists
       them; the price sorts break ties by name so two bikes at one price
       always line up the same way; newest reads the published date the feed
       carries and falls back to popularity where it is missing. */
    function centsOf(c) { return Number(c.variant.price_cents) || 0; }
    function rankOf(c) { return Number(c.data && c.data.rank) || 0; }
    function byName(a, b) { return String(a.item.title).localeCompare(String(b.item.title)) || a.order - b.order; }
    function byRank(a, b) { return rankOf(a) - rankOf(b) || a.order - b.order; }
    var SORTS = {
        popular: byRank,
        'price-asc': function (a, b) { return centsOf(a) - centsOf(b) || byName(a, b); },
        'price-desc': function (a, b) { return centsOf(b) - centsOf(a) || byName(a, b); },
        newest: function (a, b) {
            return (Number(b.data && b.data.published) || 0) - (Number(a.data && a.data.published) || 0) || byRank(a, b);
        }
    };
    /* [how the set is ordered, what the first N are] for the status line. */
    var SORT_TEXT = {
        popular: ['most popular first', 'most popular'],
        'price-asc': ['cheapest first', 'cheapest'],
        'price-desc': ['priciest first', 'priciest'],
        newest: ['newest first', 'newest']
    };

    function sortKey() {
        return SORTS[find.sort] ? find.sort : 'popular';
    }

    function setSort(key) {
        find.sort = SORTS[key] ? key : 'popular';
        if (sortSelect && sortSelect.value !== find.sort) sortSelect.value = find.sort;
    }

    /* The variant's own top tube first, then the product's: a frame that comes
       in one length carries it on the product, a 20 / 20.5 / 21 on the sizes. */
    function topTubeOf(product, variant) {
        var raw = (variant && variant.fields && variant.fields['global.top_tube_length']) ||
            (product.fields && product.fields['global.top_tube_length']) || '';
        var m = String(raw).match(/(\d+(?:\.\d+)?)/);
        return m ? parseFloat(m[1]) : NaN;
    }

    /* The top tube slider belongs to the freestyle sizes it is switched on
       for; a 24 or a race class is sized some other way and does not get it. */
    function ttActive() {
        return !!ttGroup && ttRange.ok && find.type !== 'race' && TT_SIZES.indexOf(sizeKey(find.size)) !== -1;
    }

    function syncTT() {
        if (ttGroup) ttGroup.hidden = !ttActive();
    }

    /* ---- Find bikes: filters ---------------------------------------------- */

    function fmtMoney(n) {
        n = Math.round(Number(n) || 0);
        return '$' + String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function priceText() {
        var lo = find.min, hi = find.max;
        if (lo <= 0 && !isFinite(hi)) return 'any price';
        if (lo <= 0) return 'under ' + fmtMoney(hi);
        if (!isFinite(hi)) return fmtMoney(lo) + ' and up';
        return fmtMoney(lo) + '–' + fmtMoney(hi);
    }

    function typeText() {
        return find.type === 'race' ? 'race' : 'freestyle';
    }

    function sizeText() {
        if (!find.size) return '';
        return find.type === 'race' ? find.size : find.size + '"';
    }

    function categoryText() {
        return (sizeText() + ' ' + typeText()).trim();
    }

    function numText(n) {
        return String(Math.round(n * 100) / 100);
    }

    function fmtTT(n) {
        return numText(n) + '"';
    }

    /* "20.5–21" top tube", or nothing while the slider is wide open. */
    function ttText() {
        if (!ttActive()) return '';
        var lo = find.ttLo, hi = find.ttHi;
        if (!isFinite(lo) && !isFinite(hi)) return '';
        if (!isFinite(lo)) return 'up to ' + fmtTT(hi) + ' top tube';
        if (!isFinite(hi)) return fmtTT(lo) + '+ top tube';
        return numText(lo) + '–' + fmtTT(hi) + ' top tube';
    }

    function capFirst(text) {
        return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
    }

    /* The summary bar reads the filters back as chips, so a folded bar still
       says what the columns underneath it are. */
    function updateSummary() {
        if (!findSummary) return;
        var chip = function (text, muted) {
            return '<span class="lux-cmp__find-pick' + (muted ? ' lux-cmp__find-pick--muted' : '') + '">' + esc(text) + '</span>';
        };
        if (!find.type || !find.size) {
            findSummary.innerHTML = chip(find.type ? 'Pick a size' : 'Pick a type, a size and a budget', true);
            return;
        }
        var parts = [find.type === 'race' ? 'Race' : 'Freestyle', sizeText(), capFirst(priceText())];
        var tt = ttText();
        if (tt) parts.push(capFirst(tt));
        parts.push(find.stock ? (labels.stock || 'In stock at LUXBMX') : (labels.allStock || 'Incl. supplier stock'));
        findSummary.innerHTML = parts.map(function (p) { return chip(p); }).join('');
    }

    function updatePresets() {
        presetBtns.forEach(function (btn) {
            var parts = String(btn.dataset.findPreset || '').split('-');
            var lo = Number(parts[0]) || 0;
            var hi = parts[1] === '' || parts[1] == null ? Infinity : Number(parts[1]);
            btn.classList.toggle('is-active', find.min === lo && find.max === hi);
        });
    }

    /* Two native range inputs on one track, a low handle and a high one. The
       same widget serves the price and the top tube: `floor` and `ceil` are
       the ends of the track, and a handle parked at an end means no limit on
       that side (hi becomes Infinity; lo becomes -Infinity when `openLow` is
       set, or stays at the floor for a price, where nothing is below $0). */
    function dualRange(o) {
        var minEl = root.querySelector(o.min);
        var maxEl = root.querySelector(o.max);
        var bubbleLo = root.querySelector(o.bubbleMin);
        var bubbleHi = root.querySelector(o.bubbleMax);
        var fill = root.querySelector(o.fill);
        var floor = o.floor, ceil = o.ceil, step = o.step;
        var r = { ok: !!(minEl && maxEl), lo: o.openLow ? -Infinity : floor, hi: Infinity };

        function snap(v) {
            var n = Math.round((v - floor) / step) * step + floor;
            return Number(n.toFixed(6));
        }

        r.paint = function () {
            if (!r.ok) return;
            var lo = Number(minEl.value);
            var hi = Number(maxEl.value);
            var pLo = ((lo - floor) / (ceil - floor)) * 100;
            var pHi = ((hi - floor) / (ceil - floor)) * 100;
            if (bubbleLo) { bubbleLo.textContent = o.fmtLo(lo); bubbleLo.style.left = pLo + '%'; }
            if (bubbleHi) { bubbleHi.textContent = o.fmtHi(hi); bubbleHi.style.left = pHi + '%'; }
            if (fill) { fill.style.left = pLo + '%'; fill.style.right = (100 - pHi) + '%'; }
            /* Whichever handle is nearer the far end has to be on top there,
               or the two lock together at the end of the track and only one
               moves. */
            minEl.classList.toggle('is-top', lo >= ceil - step * 2);
        };

        r.read = function (moved) {
            if (!r.ok) return;
            var lo = snap(Number(minEl.value));
            var hi = snap(Number(maxEl.value));
            if (lo > hi - step + 1e-9) {
                if (moved === 'max') {
                    hi = snap(Math.min(ceil, lo + step));
                    if (lo > hi - step + 1e-9) { lo = snap(hi - step); minEl.value = lo; }
                    maxEl.value = hi;
                } else {
                    lo = snap(Math.max(floor, hi - step));
                    minEl.value = lo;
                }
            }
            r.lo = (o.openLow && lo <= floor) ? -Infinity : lo;
            r.hi = hi >= ceil ? Infinity : hi;
            r.paint();
        };

        r.set = function (lo, hi) {
            if (!r.ok) return;
            minEl.value = isFinite(lo) ? Math.max(floor, Math.min(ceil, lo)) : (lo > 0 ? ceil : floor);
            maxEl.value = isFinite(hi) ? Math.max(floor, Math.min(ceil, hi)) : (hi > 0 ? ceil : floor);
            r.read();
        };

        return r;
    }

    var priceRange = dualRange({
        min: '[data-find-min]', max: '[data-find-max]',
        bubbleMin: '[data-find-bubble-min]', bubbleMax: '[data-find-bubble-max]', fill: '[data-find-fill]',
        floor: 0, ceil: PRICE_MAX, step: PRICE_STEP, openLow: false,
        fmtLo: fmtMoney,
        fmtHi: function (hi) { return hi >= PRICE_MAX ? fmtMoney(PRICE_MAX) + '+' : fmtMoney(hi); }
    });

    var ttRange = dualRange({
        min: '[data-find-tt-min]', max: '[data-find-tt-max]',
        bubbleMin: '[data-find-tt-bubble-min]', bubbleMax: '[data-find-tt-bubble-max]', fill: '[data-find-tt-fill]',
        floor: TT_MIN, ceil: TT_MAX, step: TT_STEP, openLow: true,
        fmtLo: fmtTT,
        fmtHi: function (hi) { return hi >= TT_MAX ? fmtTT(TT_MAX) + '+' : fmtTT(hi); }
    });

    function paintRange() {
        priceRange.paint();
        ttRange.paint();
    }

    function readRange(moved) {
        priceRange.read(moved);
        find.min = priceRange.ok ? priceRange.lo : 0;
        find.max = priceRange.ok ? priceRange.hi : Infinity;
        updatePresets();
        updateSummary();
    }

    function setRange(lo, hi) {
        priceRange.set(lo, hi);
        readRange();
    }

    function readTT(moved) {
        ttRange.read(moved);
        find.ttLo = ttRange.ok ? ttRange.lo : -Infinity;
        find.ttHi = ttRange.ok ? ttRange.hi : Infinity;
        updateSummary();
    }

    function setTT(lo, hi) {
        ttRange.set(lo, hi);
        readTT();
    }

    function selectedSize() {
        var group = sizeGroups[find.type];
        if (!group) return '';
        var checked = group.querySelector('[data-find-size]:checked');
        return checked ? checked.value : '';
    }

    function setType(type) {
        if (type !== 'race') type = 'freestyle';
        find.type = type;
        typeInputs.forEach(function (input) { input.checked = input.value === type; });
        Object.keys(sizeGroups).forEach(function (key) { sizeGroups[key].hidden = key !== type; });
        find.size = selectedSize();
        if (type === 'race') {
            loadFeed(handleFor('race', '')).then(dimRaceChips, function () { /* status reports it */ });
        }
    }

    function setSize(size) {
        var group = sizeGroups[find.type];
        if (!group) return false;
        var inputs = group.querySelectorAll('[data-find-size]');
        var found = false;
        for (var i = 0; i < inputs.length; i++) {
            var hit = sizeKey(inputs[i].value) === sizeKey(size);
            inputs[i].checked = hit;
            if (hit) found = true;
        }
        find.size = found ? size : '';
        return found;
    }

    /* A race class with nothing to sell is left in the row, dimmed, so the
       list of sizes is the same every visit and a shopper can still tap it
       and read that it is empty. */
    function dimRaceChips(products) {
        var group = sizeGroups.race;
        if (!group) return;
        var counts = {};
        products.forEach(function (product) {
            var cls = sizeKey(raceClassOf(product));
            if (!cls) return;
            var d = disciplineOf(product);
            if (d && !/race/.test(d)) return;
            (product.variants || []).forEach(function (v) {
                if (v.available !== false) counts[cls] = (counts[cls] || 0) + 1;
            });
        });
        group.querySelectorAll('[data-find-size]').forEach(function (input) {
            var label = group.querySelector('label[for="' + input.id + '"]');
            if (label) label.classList.toggle('is-dim', !counts[sizeKey(input.value)]);
        });
    }

    /* Only the type, stock and size fold; the sliders' strip below them stays
       above the table in both states. */
    function setCollapsed(state) {
        if (!findFilters || !findToggle) return;
        find.collapsed = !!state;
        findFilters.hidden = find.collapsed;
        if (findBar) findBar.classList.toggle('is-collapsed', find.collapsed);
        findToggle.setAttribute('aria-expanded', String(!find.collapsed));
        if (findToggleLabel) findToggleLabel.textContent = find.collapsed ? 'Edit filters' : 'Hide filters';
    }

    /* On a phone the filters are most of the screen, so once they have done
       their job they fold away and the table gets the room. Until the shopper
       touches the toggle themselves, that is; after that it is their call. */
    function autoCollapse() {
        if (find.userToggled || find.collapsed) return;
        if (window.innerWidth >= 1000) return;
        setCollapsed(true);
    }

    var statusTimer = null;

    function setStatus(html) {
        if (!findStatus) return;
        clearTimeout(statusTimer);
        findStatus.innerHTML = html;
        findStatus.hidden = mode !== 'find';
    }

    function flashStatus(text) {
        if (!findStatus) return;
        var before = findStatus.innerHTML;
        findStatus.innerHTML = '<strong>' + esc(text) + '</strong>';
        clearTimeout(statusTimer);
        statusTimer = setTimeout(function () { findStatus.innerHTML = before; }, 3500);
    }

    function stockLower() {
        return esc((labels.stock || 'in stock at LUXBMX').replace(/^In /, 'in '));
    }

    /* The two stock links wear the availability row's own dots - green for
       the LUXBMX shelf, blue for a supplier - so the status line and the
       table tell the shopper the same thing in the same colours. */
    var LUX_DOT = '<span class="line-availability__dot line-availability__dot--lux lux-cmp__dot" aria-hidden="true">&#9679;</span> ';
    var SUPPLIER_DOT = '<span class="line-availability__dot line-availability__dot--supplier lux-cmp__dot" aria-hidden="true">&#9679;</span> ';

    function countText(template, fallback, n) {
        return esc(String(template || fallback).replace('[count]', n));
    }

    /* The status line names the set and carries the way across the stock
       switch in either direction: with the switch on, how many the shelf is
       hiding; with it off, how many of these are actually on the shelf. The
       shelf is the better buy, and the section's wording says so ("Show the
       29 in stock at LUXBMX, ready to ship") rather than apologising for the
       count ("Only the 29..."). */
    function resultsText() {
        var n = find.all.length;
        var shown = find.columns.length;
        var html = '<strong>' + n + '</strong> ' + esc(categoryText()) + (n === 1 ? ' bike' : ' bikes');
        if (find.stock) html += ' ' + stockLower();
        html += ', ' + esc(priceText());
        var tt = ttText();
        if (tt) html += ', ' + esc(tt);
        html += ', ' + SORT_TEXT[sortKey()][0] + '.';
        if (shown < n) html += ' Showing the ' + shown + ' ' + SORT_TEXT[sortKey()][1] + '.';
        if (find.stock && find.supplierMore) {
            html += ' <button type="button" data-find-stock-off>' + SUPPLIER_DOT + countText(labels.supplierLink, '[count] more with suppliers', find.supplierMore) + '</button>';
        } else if (!find.stock && find.luxCount < n) {
            html += find.luxCount
                ? ' <button type="button" data-find-stock-on>' + LUX_DOT + countText(labels.luxLink, 'Show the [count] in stock at LUXBMX, ready to ship', find.luxCount) + '</button>'
                : ' None ' + stockLower() + ' right now.';
        }
        return html;
    }

    function emptyText() {
        var tt = ttText();
        var html = 'Nothing ' + (find.stock ? stockLower() + ' ' : '') +
            'in ' + esc(categoryText()) + ' at ' + esc(priceText()) + (tt ? ' with a ' + esc(tt) : '') + '.';
        var extra = '';
        if (find.stock && find.supplierMore) {
            extra += '<button type="button" data-find-stock-off>Include supplier stock (' + find.supplierMore + ')</button>';
        }
        if (find.min > 0 || isFinite(find.max)) {
            extra += (extra ? ' or ' : '') + '<button type="button" data-find-preset-any>widen the price range</button>';
        }
        if (tt) {
            extra += (extra ? ' or ' : '') + '<button type="button" data-find-tt-any>open up the top tube range</button>';
        }
        return emptyHtml(html, extra ? 'Try: ' + extra + '.' : '');
    }

    function renderFind() {
        if (mode !== 'find' || !findOn) return;
        syncTT();
        updateSummary();
        updatePresets();
        if (reorderHint) reorderHint.hidden = true;

        if (!find.type || !find.size) {
            setStatus('');
            setTableHtml(emptyHtml(find.type ? 'Pick a size to see what is in stock.' : 'Pick Freestyle or Race, then a size.'));
            return;
        }

        var handle = handleFor(find.type, find.size);
        var seq = ++find.seq;
        setStatus('Loading ' + esc(categoryText()) + ' bikes…');
        if (!find.feeds[handle]) setTableHtml('<p class="lux-cmp__empty">Loading</p>');

        loadFeed(handle).then(function (products) {
            if (seq !== find.seq || mode !== 'find' || panel.hidden) return;
            var result = computeFind(products);
            find.all = result.cols;
            find.supplierMore = result.supplierMore;
            find.luxCount = result.luxCount;
            find.shown = CAP;
            find.columns = find.all.slice(0, find.shown);
            if (find.type === 'race') dimRaceChips(products);
            if (!find.columns.length) {
                setStatus('');
                setTableHtml(emptyText());
                return;
            }
            renderTable(find.columns, true);
            setStatus(resultsText());
            autoCollapse();
        }, function () {
            if (seq !== find.seq || mode !== 'find') return;
            setStatus('');
            setTableHtml(emptyHtml('These bikes could not be loaded just now.', '<button type="button" data-find-retry>Try again</button>'));
        });
    }

    function showMore() {
        if (find.columns.length >= find.all.length) return;
        find.shown = find.columns.length + CAP;
        find.columns = find.all.slice(0, find.shown);
        renderTable(find.columns, true);
        setStatus(resultsText());
    }

    /* What the finder opens on, when nothing has been chosen yet this page
       load: the category of the page it was opened from, or Freestyle with
       no size for anywhere else. */
    function presetFromContext() {
        if (find.type) return;
        var type = findCfg.ctxType === 'race' ? 'race' : 'freestyle';
        var size = findCfg.ctxSize || '';
        if (type === 'race' && findCfg.ctxTitle) size = raceClassOf({ title: findCfg.ctxTitle });
        setType(type);
        if (size) setSize(size);
        find.stock = stockInput ? stockInput.checked : find.stock;
        readRange();
        readTT();
    }

    function openFind() {
        if (!findOn) return;
        presetFromContext();
        openPanel('find');
    }

    /* ---- Find bikes: the size finder -------------------------------------- */

    /* LUX's two charts, rendered into the config by the section from the
       size_guide metaobjects: freestyle (rider height -> wheel size and top
       tube) and race (class -> rider height and top tube). Both are always a
       tab away, whichever one the finder is on, because the usual mistake is
       reading a race rider off the freestyle chart or the other way round. A
       rider height lights up the matching rows and sums up both charts in a
       verdict strip, and every row carries a Find button that sets the finder
       to that type and size - on a 20 inch row, that top tube band too. */
    var guides = config.guides || {};
    var HEIGHT_KEY = 'lux-compare-height';
    var guideTab = 'freestyle';
    var guideTabs = Array.prototype.slice.call(root.querySelectorAll('[data-guide-tab]'));
    var guideHeight = root.querySelector('[data-guide-height]');
    var guideVerdict = root.querySelector('[data-guide-verdict]');

    function guideFor(type) {
        var g = guides[type === 'race' ? 'race' : 'freestyle'];
        return g && Array.isArray(g.rows) && g.rows.length ? g : null;
    }

    function colIndex(guide, pattern, fallback) {
        var cols = Array.isArray(guide.columns) ? guide.columns : [];
        for (var i = 0; i < cols.length; i++) {
            if (pattern.test(String(cols[i]))) return i;
        }
        return fallback;
    }

    /* "147 to 157cm" -> [147, 157]; "Under 120cm" -> [0, 120]; "175cm and up"
       and "170 to 185cm and up" -> [n, Infinity]; "19.5 to 19.75"" -> [19.5,
       19.75]. Anything unreadable is null and simply never matches. */
    function parseBand(text) {
        var s = String(text || '');
        var nums = (s.match(/\d+(?:\.\d+)?/g) || []).map(Number);
        if (!nums.length) return null;
        if (nums.length === 1 && /under|below|up to/i.test(s)) return [0, nums[0]];
        if (/and up|and over|and above|\+|over|plus/i.test(s)) return [nums[0], Infinity];
        if (nums.length >= 2) return [Math.min(nums[0], nums[1]), Math.max(nums[0], nums[1])];
        return [nums[0], nums[0]];
    }

    function inBand(band, h) {
        return !!band && isFinite(h) && h >= band[0] && h <= band[1];
    }

    function riderHeight() {
        var v = guideHeight ? parseFloat(guideHeight.value) : NaN;
        return isFinite(v) && v > 0 ? v : NaN;
    }

    /* Which columns are which, by header, with the two charts' shapes as the
       fallback: freestyle is height | wheel | top tube, race is class |
       height | top tube. */
    function guideCols(type, guide) {
        var race = type === 'race';
        return {
            height: colIndex(guide, /height/i, race ? 1 : 0),
            size: race ? colIndex(guide, /^size|class|frame/i, 0) : colIndex(guide, /wheel/i, 1),
            tt: colIndex(guide, /top tube/i, -1)
        };
    }

    /* The size a chart row stands for, as the finder's chips know it: "20"
       from 20", "Micro" from Micro (18" wheels). */
    function rowSize(type, cols, cells) {
        var raw = String(cells[cols.size] || '');
        if (type === 'race') return raw.replace(/\(.*?\)/g, '').trim();
        return sizeKey(raw);
    }

    function rowTT(cols, cells) {
        return cols.tt === -1 ? null : parseBand(cells[cols.tt]);
    }

    /* Only sizes the finder actually offers get a button: a chart row for a
       class or wheel size with no chip would set the finder to nothing. */
    function sizeOffered(type, size) {
        var group = sizeGroups[type === 'race' ? 'race' : 'freestyle'];
        if (!group || !size) return false;
        var inputs = group.querySelectorAll('[data-find-size]');
        for (var i = 0; i < inputs.length; i++) {
            if (sizeKey(inputs[i].value) === sizeKey(size)) return inputs[i].value;
        }
        return false;
    }

    function ttAttr(tt) {
        if (!tt) return '';
        return ' data-guide-tt="' + esc(numText(tt[0]) + '-' + (isFinite(tt[1]) ? numText(tt[1]) : '')) + '"';
    }

    /* A size button says what it is, for a shopper who has never bought a
       BMX: 16" wheels rather than 16", and a race class in the chart's own
       words (Micro (18" wheels)) rather than Micro on its own. */
    var PICK_ARROW = '<svg class="lux-cmp__gt-pick-arr" focusable="false" width="10" height="10" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

    function pickLabel(type, p) {
        if (type === 'race') return esc(p.raw || p.size);
        var label = esc(p.size + '" wheels');
        if (p.tt && TT_SIZES.indexOf(sizeKey(p.size)) !== -1) {
            label += '<small>' + esc(numText(p.tt[0]) + '–' + numText(p.tt[1]) + '" top tube') + '</small>';
        }
        return label;
    }

    /* The answer, written out. A line naming the height, then each kind of
       bike: what it is for (the section's words), and the sizes on its chart
       that cover this height as buttons that set the finder. Then what a
       button does, and the nudge for anyone still unsure which kind they are
       after. Someone buying a first bike for a child should be able to read
       it top to bottom and know what to tap. */
    function verdictHtml(h) {
        if (!isFinite(h)) return '';
        var hText = esc(numText(h));
        var html = '<p class="lux-cmp__guide-verdict-head">' + esc(labels.guideHead || 'For a [height]cm rider:').replace('[height]', hText) + '</p>';
        ['freestyle', 'race'].forEach(function (type) {
            var g = guideFor(type);
            if (!g) return;
            var name = type === 'race' ? 'Race' : 'Freestyle';
            var cols = guideCols(type, g);
            var picks = [];
            var seen = {};
            g.rows.forEach(function (cells) {
                if (!inBand(parseBand(cells[cols.height]), h)) return;
                var size = rowSize(type, cols, cells);
                var offered = sizeOffered(type, size);
                if (!offered) return;
                var tt = rowTT(cols, cells);
                var key = sizeKey(size);
                if (seen[key]) {
                    /* Two overlapping 20 inch bands: one chip, the union. */
                    var p = seen[key];
                    if (p.tt && tt) p.tt = [Math.min(p.tt[0], tt[0]), Math.max(p.tt[1], tt[1])];
                    return;
                }
                seen[key] = { size: offered, raw: String(cells[cols.size] || '').trim(), tt: tt };
                picks.push(seen[key]);
            });
            html += '<div class="lux-cmp__guide-verdict-row"><div class="lux-cmp__guide-verdict-type"><span class="lux-cmp__guide-verdict-label">' + name + '</span>';
            if (g.desc) html += '<span class="lux-cmp__guide-verdict-desc">' + esc(g.desc) + '</span>';
            html += '</div><div class="lux-cmp__guide-verdict-picks">';
            if (!picks.length) {
                html += '<span class="lux-cmp__guide-verdict-none">Nothing on the ' + name.toLowerCase() + ' chart at ' + hText + 'cm.</span>';
            } else {
                picks.forEach(function (p) {
                    html += '<button type="button" class="lux-cmp__gt-pick" data-guide-find data-guide-type="' + type + '" data-guide-size="' + esc(p.size) + '"' + ttAttr(p.tt) + '>' + pickLabel(type, p) + PICK_ARROW + '</button>';
                });
            }
            html += '</div></div>';
        });
        if (labels.guideFoot) html += '<p class="lux-cmp__guide-verdict-foot">' + esc(labels.guideFoot) + '</p>';
        return html;
    }

    function guideTableHtml(type, guide, h) {
        var cols = guideCols(type, guide);
        var html = '<div class="lux-cmp__gt-wrap"><table class="lux-cmp__gt">';
        if (Array.isArray(guide.columns) && guide.columns.length) {
            html += '<thead><tr>';
            guide.columns.forEach(function (c) { html += '<th scope="col">' + esc(c) + '</th>'; });
            html += '<th scope="col"><span class="visually-hidden">Find bikes</span></th></tr></thead>';
        }
        html += '<tbody>';
        guide.rows.forEach(function (cells) {
            if (!Array.isArray(cells)) return;
            var me = inBand(parseBand(cells[cols.height]), h);
            var size = rowSize(type, cols, cells);
            var offered = sizeOffered(type, size);
            html += '<tr' + (me ? ' class="is-me"' : '') + '>';
            cells.forEach(function (cell, i) {
                html += i === 0 ? '<th scope="row">' + esc(cell) + '</th>' : '<td>' + esc(cell) + '</td>';
            });
            html += '<td class="lux-cmp__gt-act">';
            if (offered) {
                html += '<button type="button" class="lux-cmp__gt-find" data-guide-find data-guide-type="' + type + '" data-guide-size="' + esc(offered) + '"' + ttAttr(rowTT(cols, cells)) +
                    ' aria-label="Find ' + esc(type === 'race' ? offered + ' race bikes' : offered + '&quot; freestyle bikes') + '">Find</button>';
            }
            html += '</td></tr>';
        });
        html += '</tbody></table></div>';
        return html;
    }

    function renderGuide() {
        if (!guideBody) return;
        var h = riderHeight();
        guideTabs.forEach(function (tab) {
            tab.setAttribute('aria-selected', String(tab.dataset.guideTab === guideTab));
        });
        if (guideVerdict) {
            var v = verdictHtml(h);
            guideVerdict.innerHTML = v;
            guideVerdict.hidden = !v;
        }
        var guide = guideFor(guideTab);
        if (!guide) {
            guideBody.innerHTML = '<p class="lux-cmp__guide-none">There is no ' + guideTab + ' size chart yet. Send us the rider\'s height and we will tell you what we would put them on.</p>';
            return;
        }
        var html = '';
        if (guide.intro) html += '<p class="lux-cmp__guide-intro">' + esc(guide.intro) + '</p>';
        html += guideTableHtml(guideTab, guide, h);
        if (guide.note) html += '<p class="lux-cmp__guide-note">' + esc(guide.note) + '</p>';
        if (guide.url) html += '<p class="lux-cmp__guide-more"><a href="' + esc(guide.url) + '">Read the full ' + guideTab + ' buying guide</a></p>';
        guideBody.innerHTML = html;
    }

    /* A Find button: the finder takes that type and size, and on a 20 inch
       row the top tube band as well, then the popup gets out of the way. */
    function applyGuidePick(btn) {
        var type = btn.dataset.guideType === 'race' ? 'race' : 'freestyle';
        var size = btn.dataset.guideSize || '';
        setType(type);
        setSize(size);
        if (type !== 'race') {
            var tt = String(btn.dataset.guideTt || '').split('-');
            if (tt.length === 2 && tt[0] !== '' && TT_SIZES.indexOf(sizeKey(size)) !== -1) {
                setTT(Number(tt[0]), tt[1] === '' ? Infinity : Number(tt[1]));
            } else {
                setTT(-Infinity, Infinity);
            }
        }
        closeGuide();
        updateSummary();
        renderFind();
    }

    var guideReturnFocus = null;

    function openGuide(trigger) {
        if (!guidePop || !guideBody) return;
        if (!find.type) setType('freestyle');
        guideTab = find.type === 'race' ? 'race' : 'freestyle';
        guideReturnFocus = trigger || null;
        if (guideHeight && !guideHeight.value) {
            try { guideHeight.value = localStorage.getItem(HEIGHT_KEY) || ''; } catch (e) { /* storage unavailable */ }
        }
        guidePop.hidden = false;
        renderGuide();
        if (guideCard) guideCard.focus();
    }

    function closeGuide() {
        if (!guidePop || guidePop.hidden) return;
        guidePop.hidden = true;
        if (guideReturnFocus && typeof guideReturnFocus.focus === 'function') guideReturnFocus.focus();
        guideReturnFocus = null;
    }

    /* ---- wiring ----------------------------------------------------------- */

    document.addEventListener('click', function (e) {
        var target = e.target;
        if (!target || !target.closest) return;

        var term = target.closest(POP_SEL);
        if (term) {
            e.preventDefault();
            toggleTermPop(term);
            return;
        }
        if (termPop && !termPop.hidden && !target.closest('#lux-cmp-pop')) closeTermPop();

        var add = target.closest('[data-cmp-add]');
        if (add) {
            e.preventDefault();
            /* The finder's own tick sits inside the panel, where the tray's
               "full" hint cannot be seen, so the hint moves to the status
               line there. */
            if (add.classList.contains('lux-cmp__col-add')) {
                var current = read();
                if (indexOfVariant(current, add.dataset.cmpVariant) === -1 && current.length >= config.max) {
                    flashStatus(labels.full || 'Remove one to add another.');
                    return;
                }
            }
            toggleFromButton(add);
            return;
        }

        var remove = target.closest('[data-cmp-remove]');
        if (remove) {
            e.preventDefault();
            var list = read();
            var at = indexOfVariant(list, remove.dataset.cmpRemove);
            if (at !== -1) list.splice(at, 1);
            write(list);
            renderTray();
            syncButtons();
            if (!panel.hidden && mode === 'picks') {
                if (list.length < 2 && !findOn) closePanel();
                else renderPicks();
            }
            return;
        }

        if (target.closest('[data-cmp-collapse]')) {
            var expanded = collapseBtn.getAttribute('aria-expanded') === 'true';
            collapseBtn.setAttribute('aria-expanded', String(!expanded));
            try {
                if (expanded) localStorage.setItem(COLLAPSE_KEY, '1');
                else localStorage.removeItem(COLLAPSE_KEY);
            } catch (err) { /* storage unavailable */ }
            setBodyOffset(tray.offsetHeight);
            return;
        }

        if (target.closest('[data-cmp-open]')) {
            openPanel('picks');
            return;
        }

        if (target.closest('[data-cmp-find]')) {
            e.preventDefault();
            openFind();
            return;
        }

        if (target.closest('[data-cmp-pill-dismiss]')) {
            try { sessionStorage.setItem(PILL_KEY, '1'); } catch (err) { /* storage unavailable */ }
            if (pill) pill.hidden = true;
            if (!read().length) setBodyOffset(0);
            return;
        }

        var tab = target.closest('[data-cmp-tab]');
        if (tab && root.contains(tab)) {
            if (tab.dataset.cmpTab === 'find') presetFromContext();
            switchMode(tab.dataset.cmpTab === 'find' ? 'find' : 'picks');
            return;
        }

        if (target.closest('[data-cmp-clear]')) {
            write([]);
            renderTray();
            syncButtons();
            if (!panel.hidden) {
                if (mode === 'picks' && !findOn) closePanel();
                else if (mode === 'picks') renderPicks();
            }
            return;
        }

        if (target.closest('[data-cmp-close]')) {
            closePanel();
            return;
        }

        /* Find bikes controls. */
        if (target.closest('[data-find-toggle]')) {
            find.userToggled = true;
            setCollapsed(!find.collapsed);
            return;
        }

        var nav = target.closest('[data-cmp-scroll]');
        if (nav && root.contains(nav)) {
            scrollColumns(Number(nav.dataset.cmpScroll) < 0 ? -1 : 1);
            return;
        }

        var preset = target.closest('[data-find-preset]');
        if (preset && root.contains(preset)) {
            var parts = String(preset.dataset.findPreset).split('-');
            setRange(Number(parts[0]) || 0, parts[1] === '' ? Infinity : Number(parts[1]));
            renderFind();
            return;
        }

        if (target.closest('[data-find-preset-any]')) {
            setRange(0, Infinity);
            renderFind();
            return;
        }

        if (target.closest('[data-find-stock-off]')) {
            if (stockInput) stockInput.checked = false;
            find.stock = false;
            renderFind();
            return;
        }

        if (target.closest('[data-find-stock-on]')) {
            if (stockInput) stockInput.checked = true;
            find.stock = true;
            renderFind();
            return;
        }

        if (target.closest('[data-find-tt-any]')) {
            setTT(-Infinity, Infinity);
            renderFind();
            return;
        }

        if (target.closest('[data-find-more]')) {
            showMore();
            return;
        }

        if (target.closest('[data-find-retry]')) {
            renderFind();
            return;
        }

        var guideTabBtn = target.closest('[data-guide-tab]');
        if (guideTabBtn && root.contains(guideTabBtn)) {
            guideTab = guideTabBtn.dataset.guideTab === 'race' ? 'race' : 'freestyle';
            renderGuide();
            return;
        }

        var guidePick = target.closest('[data-guide-find]');
        if (guidePick && root.contains(guidePick)) {
            applyGuidePick(guidePick);
            return;
        }

        var guideBtn = target.closest('[data-find-guide]');
        if (guideBtn) {
            openGuide(guideBtn);
            return;
        }

        if (target.closest('[data-find-guide-close]')) {
            closeGuide();
            return;
        }

        if (target === scrim) closePanel();
    });

    root.addEventListener('change', function (e) {
        var target = e.target;
        if (!target || !target.matches) return;

        if (target.matches('[data-find-type]')) {
            setType(target.value);
            updateSummary();
            renderFind();
            return;
        }

        if (target.matches('[data-find-size]')) {
            find.size = target.value;
            updateSummary();
            renderFind();
            return;
        }

        if (target.matches('[data-find-stock]')) {
            find.stock = target.checked;
            updateSummary();
            renderFind();
            return;
        }

        if (target.matches('[data-find-sort]')) {
            setSort(target.value);
            renderFind();
            return;
        }

        if (readSlider(target)) renderFind();
    });

    /* Which slider a handle belongs to, and which of its handles moved. */
    function readSlider(target) {
        if (!target || !target.matches) return false;
        if (target.matches('[data-find-min], [data-find-max]')) {
            readRange(target.matches('[data-find-max]') ? 'max' : 'min');
            return true;
        }
        if (target.matches('[data-find-tt-min], [data-find-tt-max]')) {
            readTT(target.matches('[data-find-tt-max]') ? 'max' : 'min');
            return true;
        }
        return false;
    }

    /* The slider repaints on every move and searches once the handle has
       rested a moment, so dragging across the track is not a fetch storm. The
       height box in the size finder re-lights the chart as it is typed, and
       the height is kept so the next visit starts from it. */
    var rangeTimer = null;
    root.addEventListener('input', function (e) {
        var target = e.target;
        if (target && target.matches && target.matches('[data-guide-height]')) {
            try {
                if (target.value) localStorage.setItem(HEIGHT_KEY, target.value);
                else localStorage.removeItem(HEIGHT_KEY);
            } catch (err) { /* storage unavailable */ }
            renderGuide();
            return;
        }
        if (!readSlider(target)) return;
        clearTimeout(rangeTimer);
        rangeTimer = setTimeout(renderFind, 250);
    });

    if (diffInput) {
        diffInput.addEventListener('change', function () {
            setDifferencesOnly(diffInput.checked);
            if (panel.hidden) return;
            if (mode === 'find') {
                if (find.columns.length) renderTable(find.columns, true);
            } else {
                renderPicks();
            }
        });
    }

    document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (termPop && !termPop.hidden) { closeTermPop(); return; }
        if (!panel || panel.hidden) return;
        if (guidePop && !guidePop.hidden) { closeGuide(); return; }
        closePanel();
    });

    window.addEventListener('popstate', function () {
        if (panel && !panel.hidden) closePanel(true);
    });

    /* The buy grid dispatches variant:changed on the product form whenever a
       complete variant resolves, so the button follows the size being looked
       at without this file knowing anything about option pills. */
    document.addEventListener('variant:changed', syncButtons);

    /* Tiles rewrite their own main link on a swatch click; that handler runs on
       the same click, so read the result after it. */
    document.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('a[data-card-swatch], a.card-option-pill')) {
            setTimeout(syncButtons, 0);
        }
    });

    window.addEventListener('storage', function (e) {
        if (e.key !== KEY) return;
        renderTray();
        syncButtons();
        if (!panel.hidden && mode === 'picks') renderPicks();
    });

    window.addEventListener('resize', function () {
        if (tray && !tray.hidden) setBodyOffset(tray.offsetHeight);
        else if (pill && !pill.hidden) setBodyOffset(pill.offsetHeight);
        /* On a desktop the filters have a column of their own and the fold
           button is hidden, so a fold made on a phone is undone here. */
        if (window.innerWidth >= 1000 && find.collapsed) setCollapsed(false);
        updateScrollNav();
    });

    if (findOn) {
        if (stockInput) find.stock = stockInput.checked;
        setSort(findCfg.sortDefault || 'popular');
        setCollapsed(false);
        paintRange();
        updatePresets();
    }

    renderTray();
    syncButtons();
    if (window.location.hash === '#compare') {
        openPanel(read().length >= 2 || !findOn ? 'picks' : 'find');
    } else if (window.location.hash === '#find-bikes' && findOn) {
        openFind();
    }
})();
